import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonItem,
  IonLabel,
  IonText,
  useIonViewWillLeave,
} from '@ionic/react';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneInput from '@/components/PhoneInput';
import { PhoneLogic } from '@/modules/phone';
import { MePrFoService } from '@/services/meprfo';
import { apiCall } from '@/util/api';
import {
  SECURITY_ACCOUNT_AGE_OPTIONS,
  PHONE_MIGRATION_PROOF_WINDOW_MS,
  pxToRem,
} from '@/modules/config';
import type { SecurityAccountAge } from '@/modules/config';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';

/**
 * PhoneMigrationQuestionsStep (#441): the second half of the email proof
 * method, reached once the on-file email is verified (PhoneMigrationEmailStep).
 * The user answers the two knowledge-based questions — last phone number
 * (hashed obliviously to an OPRF) + account-age bucket. Correct answers POST to
 * /api/idv/migration-proof, which mints the short-lived migration proof only
 * while the email claim is live, then advance to the new-phone step to verify
 * the new number within the window.
 */
const PhoneMigrationQuestionsStep: React.FC = () => {
  const { refreshUserContext } = useUserContext();
  const { originalPhone, navigateWithOrigin, navigateToStep, progress } =
    useProfileWizardContext();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Prefilled with the old number entered on the email step.
  const [securityPhone, setSecurityPhone] = useState(originalPhone);
  const [securityAccountAge, setSecurityAccountAge] = useState<
    SecurityAccountAge | ''
  >('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const proofWindowMinutes = Math.round(
    PHONE_MIGRATION_PROOF_WINDOW_MS / 60000
  );

  // Validate the two answers, hash the phone number obliviously (the backend
  // never sees the plaintext — same as the migration flow), and submit them to
  // the migration-proof endpoint. Correct answers come back as a fresh JWT
  // carrying the proof (applied automatically by apiCall); wrong answers get
  // the backend's message so the user can re-check them.
  const handleContinue = async () => {
    const phoneObj = PhoneLogic.tryCreate(securityPhone);
    if (!phoneObj) {
      presentToast({
        message: 'Enter the last phone number you used with Snowball.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }
    if (!securityAccountAge) {
      presentToast({
        message: 'Select when you first started using Snowball.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const { hash } = await MePrFoService.computeDeterministicHash(
        phoneObj.value
      );
      const response = await apiCall('/api/idv/migration-proof', {
        method: 'POST',
        body: JSON.stringify({
          securityAnswers: {
            phoneOprf: hash,
            accountAge: securityAccountAge,
          },
        }),
      });

      if (!response.ok) {
        let message = 'Those answers could not be verified. Please try again.';
        try {
          const err = (await response.json()) as { message?: string };
          if (err.message) message = err.message;
        } catch {
          // Non-JSON error body — keep the generic message.
        }
        presentToast({ message, color: 'danger', duration: 5000 });
        return;
      }

      // The response JWT carries the proof; refresh so the gate sees it, then
      // advance to verify the new number within the window.
      await refreshUserContext();
      navigateWithOrigin('/profile/steps/phone-migration-new-phone', 'forward');
      presentToast({
        message: `You have ${proofWindowMinutes} minutes to verify your new phone`,
        color: 'success',
        duration: 6000,
        htmlAttributes: { 'data-testid': 'phone-migration-window-toast' },
      });
    } catch {
      presentToast({
        message: 'Something went wrong. Please try again.',
        color: 'danger',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone-migration-questions')}
        progress={progress}
        onBack={() =>
          navigateToStep(
            ProfileWizardLogic.previousStep('phone-migration-questions'),
            'back'
          )
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
              Your email is verified. Answer both questions to finish proving
              it's you.
            </p>
          </IonText>
          <IonItem>
            <IonLabel position="stacked">
              Last phone number you used with Snowball
            </IonLabel>
            <PhoneInput
              value={securityPhone}
              onPhoneChange={setSecurityPhone}
              placeholder="(555) 123-4567"
              showValidation={true}
            />
          </IonItem>
          <IonText>
            <p style={{ fontSize: pxToRem(13), margin: '10px 0 0' }}>
              When did you first start using Snowball?
            </p>
          </IonText>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginTop: '8px',
            }}
          >
            {SECURITY_ACCOUNT_AGE_OPTIONS.map(option => (
              <IonButton
                key={option.value}
                size="small"
                fill={securityAccountAge === option.value ? 'solid' : 'outline'}
                onClick={() => setSecurityAccountAge(option.value)}
              >
                {option.label}
              </IonButton>
            ))}
          </div>
        </div>
      </IonContent>
      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleContinue}
            disabled={isSubmitting}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            Continue
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default PhoneMigrationQuestionsStep;
